import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Image, Text, Tooltip } from '@chakra-ui/react';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import inbound from '../../../assets/images/incomingCall.png';
import inboundDarkMode from '../../../assets/images/incomingCallDarkMode.png';
import outbound from '../../../assets/images/outgoingCall.png';
import outboundDarkMode from '../../../assets/images/outgoingCallDarkMode.png';
import { Error } from '../../../components';
import config from '../../../config.json';

/*
 * Calls Component that returns a pagenated and bufferd table with a to and from date
 * This to and from date can be chanegd and calls can be searched for
 */

import { nullError } from 'components/error';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';
import { Button } from 'components/v4';
import Grid from 'components/v4/Grid';
import useColumnState from '../../../hooks/useColumnState';
import walkthroughIds from './walkthroughIds.js';

const Calls = () => {
    const dispatch = useDispatch();
    const tableRef = useRef();
    const today = new Date();
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const lastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        today.getDate(),
    );
    const [selectedDates, setSelectedDates] = useState([lastMonth, today]);

    // state access
    const { darkMode } = useSelector((state) => {
        return { ...state.settings };
    });
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { loading: tableLoading, state: tableState } = useSelector(
        (state) => {
            return { ...state.table };
        },
    );

    const { ...ufElements } = walkthroughIds;

    // AG Grid columns
    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Date & Time',
                field: 'dateTime',
                minWidth: 250,
                maxWidth: 300,
                sortable: true,
                cellRenderer: TimeCellRenderer,
            },

            {
                headerName: 'Source Number',
                field: 'sourceNumber',
                minWidth: 200,
                cellRenderer: (params) => {
                    const raw = params.data?.sourceNumber || '';
                    const number = raw.replace(/^\+/, '');
                    return CopyCellRenderer({
                        value: `+${number}`,
                        valueFormatted: `${number}`,
                    });
                },
            },
            {
                headerName: 'Destination Number',
                field: 'destinationNumber',
                minWidth: 200,
                cellRenderer: (params) => {
                    const raw = params.data?.destinationNumber || '';
                    const number = raw.replace(/^\+/, '');
                    return CopyCellRenderer({
                        value: `+${number}`,
                        valueFormatted: `${number}`,
                    });
                },
            },
            {
                headerName: 'Direction',
                field: 'direction',
                minWidth: 120,
                cellRenderer: (params) => (
                    <Tooltip label={params.value} hasArrow>
                        <Image
                            alt=""
                            src={
                                params.value === 'Outbound'
                                    ? darkMode
                                        ? outboundDarkMode
                                        : outbound
                                    : darkMode
                                      ? inboundDarkMode
                                      : inbound
                            }
                            boxSize={5}
                        />
                    </Tooltip>
                ),
                filter: true,
            },
            {
                headerName: 'Duration',
                field: 'duration',
                minWidth: 100,
            },
        ],
        [darkMode],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableState?.data || [],
        }),
        [columns, tableState?.data],
    );

    // Convert selected dates with times to UTC ISO format
    useEffect(() => {
        if (
            selectedDates &&
            selectedDates.length === 2 &&
            selectedDates[0] &&
            selectedDates[1]
        ) {
            const fromDate = new Date(selectedDates[0]);
            const toDate = new Date(selectedDates[1]);

            // Set seconds and milliseconds to 0 for consistent timestamps
            fromDate.setSeconds(0, 0);
            toDate.setSeconds(0, 0);

            // Convert to UTC and format as yyyy-MM-ddTHH:mm:ssZ
            const fromISO = fromDate.toISOString().split('.')[0] + 'Z';
            const toISO = toDate.toISOString().split('.')[0] + 'Z';

            setFrom(fromISO);
            setTo(toISO);
        }
    }, [selectedDates]);

    // use effect to fetch the call detail record
    useEffect(() => {
        if (!currentCompany || !from || !to) return;
        if (tableLoading) {
            dispatch({
                type: 'INIT_WEB_WORKER_STATE',
                payload: {
                    fetchURL: `/calldetailrecords/${currentCompany}/${from}/${to}?page=0&limit=10000`,
                    attribute: 'callDetailRecords',
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCompany, tableLoading, from, to]);

    // onClick to fetch the call detail record
    const onClick = () => {
        dispatch({
            type: 'INIT_WEB_WORKER_STATE',
            payload: {
                fetchURL: `/calldetailrecords/${currentCompany}/${from}/${to}?page=0&limit=10000`,
                attribute: 'callDetailRecords',
            },
        });
    };

    // if no company is selected, return a alert
    if (!currentCompany) {
        return <Error error={nullError} />;
    }

    return (
        <>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Calls Report
            </Text>
            <Grid columns={2} gap={4} className="w-[600px]">
                <Box
                    sx={{
                        '& .rs-input-group': {
                            height: '32px !important',
                        },
                        '& .rs-picker-toggle': {
                            height: '32px !important',
                            minHeight: '32px !important',
                        },
                        '& .rs-picker-toggle-value': {
                            lineHeight: '24px !important',
                        },
                    }}
                    data-walkthroughid={ufElements.ufRangePickerInput}>
                    <DateRangePicker
                        value={selectedDates}
                        onChange={setSelectedDates}
                        format="MM/dd/yyyy HH:mm"
                        placeholder="Select Date & Time Range"
                        style={{ width: '100%' }}
                        menuClassName={darkMode ? 'dark-rsuite-popup' : ''}
                        cleanable={false}
                        ranges={[
                            {
                                label: 'Today',
                                value: [
                                    new Date(new Date().setHours(0, 0, 0, 0)),
                                    new Date(
                                        new Date().setHours(23, 59, 59, 999),
                                    ),
                                ],
                            },
                            {
                                label: 'Yesterday',
                                value: [
                                    new Date(
                                        new Date().setDate(
                                            new Date().getDate() - 1,
                                        ),
                                    ).setHours(0, 0, 0, 0),
                                    new Date(
                                        new Date().setDate(
                                            new Date().getDate() - 1,
                                        ),
                                    ).setHours(23, 59, 59, 999),
                                ].map((date) => new Date(date)),
                            },
                            {
                                label: 'Last 7 Days',
                                value: [
                                    new Date(
                                        new Date().setDate(
                                            new Date().getDate() - 7,
                                        ),
                                    ),
                                    new Date(),
                                ],
                            },
                        ]}
                    />
                </Box>
                <Box>
                    <Button
                        onClick={onClick}
                        size="sm"
                        data-walkthroughid={ufElements.ufSearchButton}>
                        Search
                    </Button>
                </Box>
            </Grid>
            <div style={{ height: '20px' }}></div>
            <AGGridMain
                gridRef={tableRef}
                tableData={tableDataWithColumns}
                useFixedHeight={false}
                customHeight={window.innerHeight * config.plainView.tableHeight}
                titleName="Calls"
                isLoading={tableLoading}
                onColumnVisible={onColumnVisible}
                refetch={onClick}
            />
        </>
    );
};

export default Calls;
